import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  Select,
  Spinner,
  Toggle,
  useToast,
} from "@toss-ads/ui";
import {
  createKakaoAccount,
  createKakaoForm,
  deleteKakaoAccount,
  deleteKakaoForm,
  getKakaoAuthorizeUrl,
  listAdvertisers,
  listKakaoAccounts,
  listKakaoForms,
  listPlatforms,
  syncKakaoForms,
  updateKakaoAccount,
  updateKakaoForm,
} from "../../lib/api";
import type { KakaoAccount, KakaoForm } from "../../lib/api";

type StatusMeta = {
  label: string;
  variant: "neutral" | "success" | "danger" | "warning";
};
const STATUS: Record<string, StatusMeta> = {
  pending: { label: "미인증", variant: "neutral" },
  connected: { label: "연결됨", variant: "success" },
  error: { label: "오류", variant: "danger" },
};
function statusMeta(status: string): StatusMeta {
  return STATUS[status] ?? { label: status, variant: "neutral" };
}

export function SecretsKakaoListPage() {
  const [accOpen, setAccOpen] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingAcc, setEditingAcc] = React.useState<KakaoAccount | null>(null);
  const [editingForm, setEditingForm] = React.useState<KakaoForm | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const sync = useMutation({
    mutationFn: syncKakaoForms,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "forms"] });
      toast.success(`${r.added}개 폼 추가됨 (광고주 미배정 — 수정에서 배정)`);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "찾기 실패"),
  });

  const accounts = useQuery({
    queryKey: ["admin", "kakao", "accounts"],
    queryFn: listKakaoAccounts,
  });
  const forms = useQuery({
    queryKey: ["admin", "kakao", "forms"],
    queryFn: listKakaoForms,
  });
  const platforms = useQuery({
    queryKey: ["admin", "platforms"],
    queryFn: listPlatforms,
  });
  const platformName = (id: string | null): string | null =>
    id ? (platforms.data?.find((p) => p.id === id)?.name ?? null) : null;

  return (
    <div className="space-y-8">
      {/* 1) 광고계정 (OAuth 토큰 단위) */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h1 className="text-[18px] font-semibold tracking-tight">
              카카오 광고계정
            </h1>
            <p className="text-[12px] text-ink-subtle mt-0.5">
              비즈앱(REST키·시크릿) 등록 후 <b>인증하기</b>로 비즈니스 동의 →
              토큰 발급. 5분마다 비즈폼 응답을 폴링합니다.
            </p>
          </div>
          <Button size="md" onClick={() => setAccOpen(true)}>
            + 새 광고계정
          </Button>
        </div>
        <div className="rounded-lg border bg-surface overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-surface-muted">
              <tr>
                <Th width={150}>광고계정 ID</Th>
                <Th>라벨</Th>
                <Th width={90}>상태</Th>
                <Th width={200}>인증</Th>
                <Th width={72}>수정</Th>
              </tr>
            </thead>
            <tbody>
              {accounts.isLoading && (
                <tr>
                  <td colSpan={5} className="py-10">
                    <div className="flex justify-center text-ink-subtle">
                      <Spinner size={18} />
                    </div>
                  </td>
                </tr>
              )}
              {!accounts.isLoading && (accounts.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center text-[13px] text-ink-subtle"
                  >
                    등록된 광고계정이 없습니다.
                  </td>
                </tr>
              )}
              {(accounts.data ?? []).map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <Td className="font-mono text-ink">{a.adAccountId}</Td>
                  <Td className="text-ink-muted">{a.label ?? "—"}</Td>
                  <Td>
                    <Badge variant={statusMeta(a.status).variant} dot>
                      {statusMeta(a.status).label}
                    </Badge>
                    {a.status === "error" && a.lastError && (
                      <div className="text-[11px] text-danger mt-0.5 max-w-[240px] truncate">
                        {a.lastError}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <AuthorizeButton account={a} />
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingAcc(a)}
                    >
                      수정
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 2) 비즈폼 ↔ 광고주 매핑 */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight">
              비즈폼 매핑 (폼 → 광고주)
            </h2>
            <p className="text-[12px] text-ink-subtle mt-0.5">
              formId 를 수집 광고주(버킷)에 연결. 활성 + 광고주 배정된 폼만 폴링합니다.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="md"
              variant="outline"
              loading={sync.isPending}
              disabled={(accounts.data ?? []).length === 0}
              onClick={() => sync.mutate()}
            >
              전부 찾기
            </Button>
            <Button
              size="md"
              onClick={() => setFormOpen(true)}
              disabled={(accounts.data ?? []).length === 0}
            >
              + 새 폼
            </Button>
          </div>
        </div>
        <div className="rounded-lg border bg-surface overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-surface-muted">
              <tr>
                <Th width={150}>광고주</Th>
                <Th width={140}>formId</Th>
                <Th>라벨</Th>
                <Th width={150}>소스(표시)</Th>
                <Th width={80}>활성</Th>
                <Th width={140}>마지막 폴링</Th>
                <Th width={72}>수정</Th>
              </tr>
            </thead>
            <tbody>
              {forms.isLoading && (
                <tr>
                  <td colSpan={7} className="py-10">
                    <div className="flex justify-center text-ink-subtle">
                      <Spinner size={18} />
                    </div>
                  </td>
                </tr>
              )}
              {!forms.isLoading && (forms.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-[13px] text-ink-subtle"
                  >
                    등록된 폼이 없습니다.
                  </td>
                </tr>
              )}
              {(forms.data ?? []).map((f) => (
                <tr key={f.id} className="border-t border-border">
                  <Td>
                    {f.advertiserName ? (
                      <span className="text-ink">{f.advertiserName}</span>
                    ) : (
                      <span className="text-warning text-[12px]">미배정</span>
                    )}
                  </Td>
                  <Td className="font-mono text-ink">{f.formId}</Td>
                  <Td className="text-ink-muted">{f.label ?? "—"}</Td>
                  <Td>
                    {f.sourceLabel ? (
                      <div className="leading-tight">
                        <div className="text-ink">{f.sourceLabel}</div>
                        <div className="text-[11px] text-ink-subtle">
                          {platformName(f.platformId) ?? "카카오"}
                        </div>
                      </div>
                    ) : f.platformId ? (
                      <span className="text-ink">
                        {platformName(f.platformId) ?? "매체"}
                      </span>
                    ) : (
                      <Badge variant="neutral">카카오</Badge>
                    )}
                  </Td>
                  <Td>
                    <Badge variant={f.active ? "success" : "neutral"} dot>
                      {f.active ? "활성" : "중지"}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">
                    {f.lastPolledAt ? formatDateTime(f.lastPolledAt) : "—"}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingForm(f)}
                    >
                      수정
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CreateAccountModal open={accOpen} onClose={() => setAccOpen(false)} />
      <EditAccountModal
        account={editingAcc}
        onClose={() => setEditingAcc(null)}
      />
      <CreateFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        accounts={accounts.data ?? []}
      />
      <EditFormModal form={editingForm} onClose={() => setEditingForm(null)} />
    </div>
  );
}

function AuthorizeButton({ account }: { account: KakaoAccount }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["admin", "kakao", "accounts"] });
  }, [qc]);

  return (
    <Button
      size="sm"
      variant={account.status === "connected" ? "outline" : "primary"}
      loading={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const { url } = await getKakaoAuthorizeUrl(account.id);
          const win = window.open(url, "kakao_oauth");
          // 콜백 완료 알림 수신 시 즉시 갱신.
          const onMsg = (e: MessageEvent) => {
            if (e.data === "kakao-oauth-done") refresh();
          };
          window.addEventListener("message", onMsg);
          // 팝업이 닫히면 갱신 + 리스너 정리.
          const timer = window.setInterval(() => {
            if (!win || win.closed) {
              window.clearInterval(timer);
              window.removeEventListener("message", onMsg);
              refresh();
            }
          }, 800);
        } catch (e) {
          toast.error(e instanceof ApiError ? e.message : "URL 발급 실패");
        } finally {
          setLoading(false);
        }
      }}
    >
      {account.status === "connected" ? "재인증" : "인증하기"}
    </Button>
  );
}

function CreateAccountModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adAccountId, setAdAccountId] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [restApiKey, setRestApiKey] = React.useState("");
  const [clientSecret, setClientSecret] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setAdAccountId("");
      setLabel("");
      setRestApiKey("");
      setClientSecret("");
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createKakaoAccount({
        adAccountId: adAccountId.trim(),
        label: label.trim() || undefined,
        restApiKey: restApiKey.trim(),
        clientSecret: clientSecret.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "accounts"] });
      toast.success("광고계정이 등록되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "등록 실패"),
  });

  const valid = adAccountId.trim() && restApiKey.trim() && clientSecret.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="카카오 광고계정 등록"
      description="REST API 키·client_secret 은 비즈앱마다 다릅니다. 시크릿은 암호화 저장됩니다."
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="ka-acc">광고계정 ID (adAccountId)</Label>
          <Input
            id="ka-acc"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ka-label">라벨 (선택)</Label>
          <Input
            id="ka-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ka-key">REST API 키 (client_id)</Label>
          <Input
            id="ka-key"
            className="font-mono text-[12px]"
            value={restApiKey}
            onChange={(e) => setRestApiKey(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ka-secret">client_secret</Label>
          <Input
            id="ka-secret"
            className="font-mono text-[12px]"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!valid}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          등록
        </Button>
      </div>
    </Modal>
  );
}

function EditAccountModal({
  account,
  onClose,
}: {
  account: KakaoAccount | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [label, setLabel] = React.useState("");
  const [clientSecret, setClientSecret] = React.useState("");
  const [confirmDel, setConfirmDel] = React.useState(false);

  React.useEffect(() => {
    if (account) {
      setLabel(account.label ?? "");
      setClientSecret("");
      setConfirmDel(false);
    }
  }, [account]);

  const save = useMutation({
    mutationFn: () =>
      updateKakaoAccount(account!.id, {
        label: label.trim(),
        clientSecret: clientSecret.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "accounts"] });
      toast.success("저장되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "저장 실패"),
  });

  const del = useMutation({
    mutationFn: () => deleteKakaoAccount(account!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "accounts"] });
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "forms"] });
      toast.success("삭제되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });

  return (
    <Modal
      open={!!account}
      onClose={onClose}
      title="광고계정 수정"
      description={account ? `adAccountId: ${account.adAccountId}` : undefined}
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="ka-elabel">라벨</Label>
          <Input
            id="ka-elabel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ka-esecret">client_secret 교체 (선택)</Label>
          <Input
            id="ka-esecret"
            className="font-mono text-[12px]"
            placeholder="변경 시에만 입력"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-4 mt-4 border-t border-border">
        {confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-danger">폼도 함께 삭제됨. 삭제?</span>
            <Button
              size="sm"
              variant="danger"
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              삭제 확정
            </Button>
            <Button size="sm" variant="text" onClick={() => setConfirmDel(false)}>
              취소
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="text"
            className="text-danger"
            onClick={() => setConfirmDel(true)}
          >
            삭제
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="text" size="md" onClick={onClose}>
            닫기
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateFormModal({
  open,
  onClose,
  accounts,
}: {
  open: boolean;
  onClose: () => void;
  accounts: KakaoAccount[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [accountId, setAccountId] = React.useState("");
  const [advertiserId, setAdvertiserId] = React.useState("");
  const [formId, setFormId] = React.useState("");
  const [sourceLabel, setSourceLabel] = React.useState("");
  const [platformId, setPlatformId] = React.useState("");

  const { data: advertisers } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
  });
  const platforms = useQuery({
    queryKey: ["admin", "platforms"],
    queryFn: listPlatforms,
  });

  React.useEffect(() => {
    if (open) {
      setAccountId(accounts[0]?.id ?? "");
      setAdvertiserId("");
      setFormId("");
      setSourceLabel("");
      setPlatformId("");
    }
  }, [open, accounts]);

  const create = useMutation({
    mutationFn: () =>
      createKakaoForm({
        accountId,
        advertiserId,
        formId: formId.trim(),
        // 매체는 필수. 라벨은 선택(있으면 매체 위 2줄).
        platformId,
        sourceLabel: sourceLabel.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "forms"] });
      toast.success("폼이 등록되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "등록 실패"),
  });

  const valid = accountId && advertiserId && formId.trim() && platformId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="비즈폼 매핑 등록"
      description="폼(formId)을 수집 광고주에 연결합니다."
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="kf-acc">광고계정</Label>
          <Select
            id="kf-acc"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label ?? a.adAccountId}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="kf-adv">광고주</Label>
          <Select
            id="kf-adv"
            value={advertiserId}
            onChange={(e) => setAdvertiserId(e.target.value)}
          >
            <option value="">선택…</option>
            {(advertisers ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="kf-form">formId</Label>
          <Input
            id="kf-form"
            className="font-mono"
            value={formId}
            onChange={(e) => setFormId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="kf-plat">매체 매핑 (필수)</Label>
          <Select
            id="kf-plat"
            value={platformId}
            onChange={(e) => setPlatformId(e.target.value)}
          >
            <option value="">선택…</option>
            {(platforms.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-ink-subtle mt-1">
            이 폼 리드의 매체. 반드시 지정해야 등록됩니다.
          </p>
        </div>
        <div>
          <Label htmlFor="kf-src">리드 표시명 (선택)</Label>
          <Input
            id="kf-src"
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            placeholder="비우면 매체명만 1줄 표시"
            autoComplete="off"
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            입력 시 '라벨 / 매체명' 2줄로 표기. 비우면 매체명 1줄.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!valid}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          등록
        </Button>
      </div>
    </Modal>
  );
}

function EditFormModal({
  form,
  onClose,
}: {
  form: KakaoForm | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [advertiserId, setAdvertiserId] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [sourceLabel, setSourceLabel] = React.useState("");
  const [platformId, setPlatformId] = React.useState("");
  const [confirmDel, setConfirmDel] = React.useState(false);

  const { data: advertisers } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
  });
  const platforms = useQuery({
    queryKey: ["admin", "platforms"],
    queryFn: listPlatforms,
  });

  React.useEffect(() => {
    if (form) {
      setAdvertiserId(form.advertiserId ?? "");
      setActive(form.active);
      setSourceLabel(form.sourceLabel ?? "");
      setPlatformId(form.platformId ?? "");
      setConfirmDel(false);
    }
  }, [form]);

  const save = useMutation({
    mutationFn: () =>
      updateKakaoForm(form!.id, {
        advertiserId: advertiserId || undefined,
        active,
        // 매체는 필수. 라벨은 선택(있으면 매체 위 2줄).
        platformId,
        sourceLabel: sourceLabel.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "forms"] });
      toast.success("저장되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "저장 실패"),
  });

  const del = useMutation({
    mutationFn: () => deleteKakaoForm(form!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kakao", "forms"] });
      toast.success("삭제되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });

  return (
    <Modal
      open={!!form}
      onClose={onClose}
      title="비즈폼 매핑 수정"
      description={form ? form.formId : undefined}
    >
      <div className="space-y-3">
        <div>
          <Label>폼 (자동 발견)</Label>
          <div className="text-[13px] text-ink">
            {form?.label || "—"}{" "}
            <span className="font-mono text-[12px] text-ink-subtle">
              {form?.formId}
            </span>
          </div>
        </div>
        <div>
          <Label htmlFor="kf-eadv">광고주</Label>
          <Select
            id="kf-eadv"
            value={advertiserId}
            onChange={(e) => setAdvertiserId(e.target.value)}
          >
            <option value="">미배정</option>
            {(advertisers ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="kf-eplat">매체 매핑 (필수)</Label>
          <Select
            id="kf-eplat"
            value={platformId}
            onChange={(e) => setPlatformId(e.target.value)}
          >
            <option value="">선택…</option>
            {(platforms.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-ink-subtle mt-1">
            이 폼 리드의 매체. 필수. (기존 리드 소급 반영은 백필 필요)
          </p>
        </div>
        <div>
          <Label htmlFor="kf-esrc">리드 표시명 (선택)</Label>
          <Input
            id="kf-esrc"
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            placeholder="비우면 매체명만 1줄 표시"
            autoComplete="off"
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            입력 시 '라벨 / 매체명' 2줄로 표기. 비우면 매체명 1줄.
          </p>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div>
            <div className="text-[13px] text-ink">활성</div>
            <div className="text-[11px] text-ink-subtle">
              끄면 이 폼 폴링을 중지합니다.
            </div>
          </div>
          <Toggle checked={active} onCheckedChange={setActive} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-4 mt-4 border-t border-border">
        {confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-danger">삭제?</span>
            <Button
              size="sm"
              variant="danger"
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              삭제 확정
            </Button>
            <Button size="sm" variant="text" onClick={() => setConfirmDel(false)}>
              취소
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="text"
            className="text-danger"
            onClick={() => setConfirmDel(true)}
          >
            삭제
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="text" size="md" onClick={onClose}>
            닫기
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!platformId}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Th({ children, width }: { children: React.ReactNode; width?: number }) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`px-2.5 py-3 align-middle ${className ?? ""}`}>{children}</td>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}.${day} ${hh}:${mm}`;
}
